import { Paths } from '@/app/paths';
import trackUserAction from '@/infrastructure/sentry/useTrackEvent';
import useWidgetCurrency from '@/infrastructure/state/WidgetCurrency/useWidgetCurrency';
import {
  Combobox,
  Group,
  Input,
  InputBase,
  Text,
  ThemeIcon,
  useCombobox
} from '@mantine/core';
import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import styles from './index.module.css';

interface IProps {
  withName?: boolean;
  fullWidth?: boolean;
  onChange?: () => void;
}

const CurrencyPicker: FC<IProps> = ({ fullWidth, withName, onChange }) => {
  const { widgetCurrencies, widgetCurrency, setWidgetCurrency } =
    useWidgetCurrency();

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption()
  });

  const currencyOptions = useMemo(() => {
    return widgetCurrencies.map(currency => ({
      value: currency.code,
      label: withName
        ? currency.name.toUpperCase()
        : currency.code.toUpperCase(),
      icon: currency.symbol,
      name: currency.name
    }));
  }, []);

  const options = currencyOptions.map(item => (
    <Combobox.Option value={item.value} key={item.value}>
      <Group gap="2px">
        <ThemeIcon size="sm" radius={20} mr={3}>
          <Text className={styles.optionIcon}>{item.icon}</Text>
        </ThemeIcon>
        {item.label}
      </Group>
    </Combobox.Option>
  ));

  return (
    <Combobox
      variant="outline-invert"
      radius="sm"
      width={fullWidth ? undefined : '100'}
      position="bottom-end"
      store={combobox}
      onOptionSubmit={val => {
        setWidgetCurrency(widgetCurrencies.find(wc => wc.code === val));
        combobox.closeDropdown();
        onChange?.();
        trackUserAction(`Clicked on change widget currency to ${val}`);
      }}
    >
      <Combobox.Target>
        <InputBase
          id="currency-picker"
          radius="md"
          classNames={{ input: styles.comboBoxInput }}
          className={styles.currencyPicker}
          variant="outline-invert"
          component="button"
          type="button"
          pointer
          h="34px"
          mih="34px"
          style={{ height: '34px', minHeight: '34px' }}
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
        >
          {widgetCurrency ? (
            <div className={styles.valueWrapper}>
              <ThemeIcon size="sm" radius="20px" variant="inverted">
                <Text>{widgetCurrency.symbol}</Text>
              </ThemeIcon>
              {withName && widgetCurrency.name}
            </div>
          ) : (
            <Input.Placeholder>Currency</Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default CurrencyPicker;
